"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Home as HomeIcon,
  People as UsersIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as DollarIcon,
  BarChart as BarChartIcon,
  Webhook as WebhookIcon,
  ShoppingBag as ShoppingBagIcon,
  Inventory as PackageIcon,
  Person as PersonIcon,
  ExpandMore as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  PaymentOutlined as PaymentIcon,
} from "@mui/icons-material"
import { Box, Typography, useTheme, alpha, Paper } from "@mui/material"
import { useUIContext } from "../../contexts/UIContext"
import Logo from "../brand/Logo"
import OrganizationSelector from "../common/OrganizationSelector"
import { useAuth } from "../../contexts/AuthContext"

const Sidebar = () => {
  const theme = useTheme()
  const location = useLocation()
  const { isSidebarOpen, toggleSidebar } = useUIContext()
  const [expandedMenus, setExpandedMenus] = useState({})
  const { user } = useAuth()

  // Check if user is superadmin
  const isSuperAdmin = user && user.role === "superadmin"

  // Modify the menuItems array to include payment recovery as a dedicated section
  const menuItems = [
    {
      id: "dashboard",
      title: "Dashboard",
      icon: <HomeIcon fontSize="small" />,
      path: "/dashboard",
      submenu: null,
    },
    {
      id: "analytics",
      title: "Analytics",
      icon: <BarChartIcon fontSize="small" />,
      path: "/analytics",
      submenu: null,
    },
    {
      id: "users",
      title: "Users",
      icon: <UsersIcon fontSize="small" />,
      path: "/users",
      submenu: [
        { title: "All Users", path: "/users" },
        { title: "Create User", path: "/users/create" },
      ],
    },
    {
      id: "customers",
      title: "Customers",
      icon: <ShoppingBagIcon fontSize="small" />,
      path: "/customers",
      submenu: [
        { title: "All Customers", path: "/customers" },
        { title: "Create Customer", path: "/customers/create" },
      ],
    },
    {
      id: "subscriptions",
      title: "Subscriptions",
      icon: <PackageIcon fontSize="small" />,
      path: "/subscriptions",
      submenu: [
        { title: "All Subscriptions", path: "/subscriptions" },
        { title: "Plans", path: "/plans" },
      ],
    },
    {
      id: "credits",
      title: "Credits",
      icon: <CreditCardIcon fontSize="small" />,
      path: "/credits",
      submenu: [
        { title: "Organization Credits", path: "/credits" },
        { title: "Customer Credits", path: "/credits/customer" },
        { title: "Credit Usage", path: "/credits/usage" },
      ],
    },
    {
      id: "billing",
      title: "Billing",
      icon: <DollarIcon fontSize="small" />,
      path: "/billing",
      submenu: [
        { title: "Invoices", path: "/billing/invoices" },
        { title: "Payment Methods", path: "/billing/payment-methods" },
      ],
    },
    {
      id: "reports",
      title: "Reports",
      icon: <BarChartIcon fontSize="small" />,
      path: "/reports",
      submenu: [
        { title: "Revenue Report", path: "/reports/revenue" },
        { title: "Credit Report", path: "/reports/credits" },
      ],
    },
    {
      id: "webhooks",
      title: "Webhooks",
      icon: <WebhookIcon fontSize="small" />,
      path: "/webhooks",
      submenu: null,
    },
    // New Payment Recovery section
    {
      id: "payment-recovery",
      title: "Payment Recovery",
      icon: <PaymentIcon fontSize="small" />,
      path: "/payment-recovery",
      submenu: [
        { title: "Settings", path: "/payment-recovery/settings" },
        { title: "Email Templates", path: "/payment-recovery/email-templates" },
        { title: "Recovery Analytics", path: "/payment-recovery/analytics" },
        { title: "Failed Payments", path: "/payment-recovery/failed-payments" },
      ],
    },
    {
      id: "settings",
      title: "Settings",
      icon: <PersonIcon fontSize="small" />,
      path: "/settings",
      submenu: [
        { title: "Profile", path: "/settings/profile" },
        { title: "Organization", path: "/settings/organization" },
        { title: "Email Templates", path: "/settings/email-templates" },
      ],
    },
  ]

  // Check if path is active - more specific to avoid highlighting parent when child is active
  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path
    }
    // For parent items, only highlight if it's an exact match or if no submenu item is active
    return location.pathname === path
  }

  // Check if submenu item is active
  const isSubmenuItemActive = (path) => {
    return location.pathname === path
  }

  // Check if any submenu item is active for a parent
  const isAnySubmenuItemActive = (submenuItems) => {
    return submenuItems && submenuItems.some((item) => location.pathname === item.path)
  }

  // Initialize expanded menus based on current path
  useEffect(() => {
    const newExpandedMenus = {}
    menuItems.forEach((item) => {
      if (item.submenu && item.submenu.some((subItem) => location.pathname === subItem.path)) {
        newExpandedMenus[item.id] = true
      }
    })
    setExpandedMenus(newExpandedMenus)
  }, [location.pathname])

  // Toggle submenu - close other menus when opening a new one
  const toggleSubmenu = (id) => {
    setExpandedMenus((prev) => {
      // If we're opening this menu, close all others
      if (!prev[id]) {
        const result = {}
        result[id] = true
        return result
      }
      // If we're closing this menu, just close it
      else {
        const result = { ...prev }
        delete result[id]
        return result
      }
    })
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100%",
        width: isSidebarOpen ? 250 : 70,
        bgcolor: "#FFFFFF",
        borderRight: "1px solid #F0F0F0",
        zIndex: 1200,
        transition: "width 0.3s ease",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Logo size={isSidebarOpen ? "md" : "sm"} withText={isSidebarOpen} />
        </Box>

        {/* Add toggle button */}
        <Box
          onClick={() => toggleSidebar()}
          sx={{
            cursor: "pointer",
            color: "#637381",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {isSidebarOpen ? (
            <ChevronDownIcon sx={{ transform: "rotate(-90deg)" }} />
          ) : (
            <ChevronDownIcon sx={{ transform: "rotate(90deg)" }} />
          )}
        </Box>
      </Box>

      {isSuperAdmin && isSidebarOpen && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #F0F0F0",
          }}
        >
          <OrganizationSelector />
        </Box>
      )}

      <Box
        sx={{
          overflowY: "auto",
          height: isSuperAdmin ? "calc(100vh - 120px)" : "calc(100vh - 64px)",
          px: 2,
          py: 1,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F5F5F5",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#DDDDDD",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#BBBBBB",
          },
        }}
      >
        <Box component="nav">
          {/* Main Navigation Items */}
          {menuItems.map((item) => {
            // Determine if this parent item should be highlighted
            const hasActiveSubmenuItem = item.submenu && isAnySubmenuItemActive(item.submenu)
            const shouldHighlightParent = isActive(item.path, true) && !hasActiveSubmenuItem

            return (
              <Box key={item.id} sx={{ mb: 0.5 }}>
                {item.submenu ? (
                  <Box>
                    <Box
                      component={isSidebarOpen ? "button" : Link}
                      to={!isSidebarOpen ? item.path : undefined}
                      onClick={isSidebarOpen ? () => toggleSubmenu(item.id) : undefined}
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isSidebarOpen ? "space-between" : "center",
                        py: 1.2,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: "transparent",
                        color: shouldHighlightParent ? theme.palette.primary.main : "#637381",
                        transition: "all 0.2s",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        textDecoration: "none",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            mr: isSidebarOpen ? 1.5 : 0,
                            color: shouldHighlightParent ? theme.palette.primary.main : "#637381",
                          }}
                        >
                          {item.icon}
                        </Box>
                        {isSidebarOpen && (
                          <Typography
                            variant="body2"
                            fontWeight={shouldHighlightParent ? 600 : 500}
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {item.title}
                          </Typography>
                        )}
                      </Box>
                      {isSidebarOpen &&
                        (expandedMenus[item.id] ? (
                          <ChevronDownIcon
                            fontSize="small"
                            sx={{
                              color: shouldHighlightParent ? theme.palette.primary.main : "#637381",
                              fontSize: "1.2rem",
                            }}
                          />
                        ) : (
                          <ChevronRightIcon
                            fontSize="small"
                            sx={{
                              color: shouldHighlightParent ? theme.palette.primary.main : "#637381",
                              fontSize: "1.2rem",
                            }}
                          />
                        ))}
                    </Box>

                    {isSidebarOpen && expandedMenus[item.id] && (
                      <Box
                        sx={{
                          pl: 4.5,
                          pr: 1,
                          overflow: "hidden",
                          maxHeight: expandedMenus[item.id] ? "500px" : "0px",
                          transition: "max-height 0.3s ease",
                        }}
                      >
                        {item.submenu.map((subItem) => (
                          <Box key={subItem.path} sx={{ mb: 0.5 }}>
                            <Box
                              component={Link}
                              to={subItem.path}
                              sx={{
                                display: "block",
                                py: 0.8,
                                px: 1.5,
                                borderRadius: 1,
                                textDecoration: "none",
                                color: isSubmenuItemActive(subItem.path) ? theme.palette.primary.main : "#637381",
                                bgcolor: isSubmenuItemActive(subItem.path)
                                  ? alpha(theme.palette.primary.main, 0.08)
                                  : "transparent",
                                transition: "all 0.2s",
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isSubmenuItemActive(subItem.path) ? 500 : 400,
                                  fontSize: "0.8125rem",
                                }}
                              >
                                {subItem.title}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    component={Link}
                    to={item.path}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isSidebarOpen ? "flex-start" : "center",
                      py: 1.2,
                      px: 1.5,
                      borderRadius: 1,
                      textDecoration: "none",
                      color: isActive(item.path) ? theme.palette.primary.main : "#637381",
                      bgcolor: isActive(item.path) ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        mr: isSidebarOpen ? 1.5 : 0,
                        color: isActive(item.path) ? theme.palette.primary.main : "#637381",
                      }}
                    >
                      {item.icon}
                    </Box>
                    {isSidebarOpen && (
                      <Typography
                        variant="body2"
                        fontWeight={isActive(item.path) ? 600 : 500}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {item.title}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )
          })}

          {/* User info at bottom */}
          {isSidebarOpen && user && (
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #F0F0F0",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: theme.palette.primary.main,
                  color: "#FFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {user.name ? user.name.charAt(0).toUpperCase() : "U"}
              </Box>
              <Typography variant="body2" fontWeight={500} noWrap>
                {user.name || user.email}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

export default Sidebar

